<div
  class="cart-drawer__backdrop"
  [class.cart-drawer__backdrop--visible]="isOpen()"
  aria-hidden="true"
  (click)="onClose()"
></div>

<aside
  #drawerEl
  class="cart-drawer"
  [class.cart-drawer--open]="isOpen()"
  role="dialog"
  aria-modal="true"
  aria-label="Carrito de compras"
  tabindex="-1"
>
  <header class="cart-drawer__header">
    <h2 class="cart-drawer__title">
      Tu carrito
      @if (itemCount() > 0) {
        <span class="cart-drawer__count">({{ itemCount() }})</span>
      }
    </h2>
    <button
      #closeBtn
      class="cart-drawer__close"
      type="button"
      aria-label="Cerrar carrito"
      (click)="onClose()"
    >
      <app-icon [icon]="closeIcon" [size]="20" color="currentColor" />
    </button>
  </header>

  <div class="cart-drawer__body">
    @if (isEmpty()) {
      <div class="cart-drawer__empty">
        <img
          class="cart-drawer__empty-mascot"
          [src]="mascotUrl"
          alt=""
          loading="lazy"
        />
        <h3 class="cart-drawer__empty-title">Tu carrito está vacío</h3>
        <p class="cart-drawer__empty-subtitle">Aún no has añadido productos</p>
        <app-button variant="primary" (click)="onExploreClick()">
          Explorar tienda
        </app-button>
      </div>
    } @else {
      <ul class="cart-drawer__lines" role="list">
        @for (line of cart()?.lines ?? []; track line.id) {
          <li>
            <article
              class="cart-drawer__line"
              [class.cart-drawer__line--pending]="isLinePending(line.id)"
              [class.cart-drawer__line--conflict]="lineConflict(line)"
              [class.cart-drawer__line--blocked]="isLineBlocked(line.id)"
            >
              <img
                class="cart-drawer__line-img"
                [src]="lineImageUrl(line)"
                [alt]="line.name"
                loading="lazy"
                (error)="onImageError($event)"
              />

              <div class="cart-drawer__line-info">
                <div class="cart-drawer__line-heading">
                  <p class="cart-drawer__line-name">{{ line.name }}</p>
                  @if (isLineBlocked(line.id)) {
                    <app-badge variant="vip">{{ blockedCopy.label }}</app-badge>
                  }
                  @if (lineCoinsChip(line); as chip) {
                    <app-badge variant="highlight">{{ chip }}</app-badge>
                  }
                  @if (lineHasOffer(line)) {
                    <app-badge variant="sale">-{{ lineDiscountPct(line) }}%</app-badge>
                  }
                </div>
                <p class="cart-drawer__line-price">
                  @if (lineOriginalPrice(line); as original) {
                    <span class="cart-drawer__line-original">{{ formatCRC(original) }}</span>
                  }
                  <span class="cart-drawer__line-unit">{{ formatCRC(lineUnitPrice(line)) }}</span>
                </p>

                <div class="cart-drawer__qty">
                  <button
                    class="cart-drawer__qty-btn"
                    type="button"
                    [attr.aria-label]="'Disminuir cantidad de ' + line.name"
                    [attr.title]="lineIsCoinOpportunity(line) ? qtyFixedCopy : null"
                    [disabled]="isLinePending(line.id) || lineIsCoinOpportunity(line)"
                    (click)="onDecrement(line)"
                  >
                    <app-icon [icon]="minusIcon" [size]="14" color="currentColor" />
                  </button>
                  <span class="cart-drawer__qty-value" aria-live="polite">
                    {{ line.quantity }}
                  </span>
                  <!-- Subir está inhabilitado en una línea bloqueada (R9b);
                       bajar y quitar siguen siempre disponibles. Una línea de
                       oportunidad con Coins es de 1 unidad (D107): sin stepper,
                       solo quitar. -->
                  <button
                    class="cart-drawer__qty-btn"
                    type="button"
                    [attr.aria-label]="'Aumentar cantidad de ' + line.name"
                    [attr.title]="lineIsCoinOpportunity(line) ? qtyFixedCopy : null"
                    [disabled]="isLinePending(line.id) || isLineBlocked(line.id) || lineIsCoinOpportunity(line)"
                    (click)="onIncrement(line)"
                  >
                    <app-icon [icon]="plusIcon" [size]="14" color="currentColor" />
                  </button>
                </div>

                @if (isLineBlocked(line.id)) {
                  <div class="cart-drawer__line-blocked" role="alert">
                    <p class="cart-drawer__line-blocked-text">{{ blockedReason(line.id) }}</p>
                    <button
                      class="cart-drawer__line-conflict-cta"
                      type="button"
                      [disabled]="isLinePending(line.id)"
                      (click)="onRemove(line)"
                    >
                      {{ blockedCopy.removeCta }}
                    </button>
                  </div>
                }

                @if (lineConflict(line); as conflict) {
                  <div class="cart-drawer__line-conflict" role="alert">
                    <p class="cart-drawer__line-conflict-text">
                      {{ conflictFreeLabel(conflict.available) }}
                    </p>
                    <button
                      class="cart-drawer__line-conflict-cta"
                      type="button"
                      [disabled]="isLinePending(line.id)"
                      (click)="onLeaveFree(line, conflict.available)"
                    >
                      @if (conflict.available > 0) {
                        {{ conflictAdjustLabel(conflict.available) }}
                      } @else {
                        {{ conflictCopy.conflictRemoveCta }}
                      }
                    </button>
                  </div>
                }
              </div>

              <div class="cart-drawer__line-side">
                <p class="cart-drawer__line-subtotal">{{ formatCRC(lineSubtotal(line)) }}</p>
                <button
                  class="cart-drawer__line-remove"
                  type="button"
                  [attr.aria-label]="'Eliminar ' + line.name + ' del carrito'"
                  [disabled]="isLinePending(line.id)"
                  (click)="onRemove(line)"
                >
                  <app-icon [icon]="trashIcon" [size]="16" color="currentColor" />
                </button>
              </div>
            </article>
          </li>
        }
      </ul>

      <section class="cart-drawer__totals" aria-label="Resumen">
        <!-- Subtotal de productos en BRUTO (con IVA, pre-descuento) = total + discounts;
             cart.subtotal/taxes del API vienen YA descontados (IVA sobre base descontada). -->
        <div class="cart-drawer__totals-row">
          <span>Subtotal</span>
          <span>{{ formatCRC((cart()?.total ?? 0) + (cart()?.discounts ?? 0)) }}</span>
        </div>
        @if ((cart()?.discounts ?? 0) > 0) {
          <div class="cart-drawer__totals-row cart-drawer__totals-row--discount">
            <span>Descuento</span>
            <span>−{{ formatCRC(cart()?.discounts ?? 0) }}</span>
          </div>
        }
        <div class="cart-drawer__totals-row">
          <span>Envío</span>
          <span class="cart-drawer__totals-shipping">Calculado en checkout</span>
        </div>
        <div class="cart-drawer__totals-row cart-drawer__totals-row--total">
          <span>Total</span>
          <span>{{ formatCRC(cart()?.total ?? 0) }}</span>
        </div>
        @if ((cart()?.taxes ?? 0) > 0) {
          <div class="cart-drawer__totals-row cart-drawer__totals-row--tax-note">
            <span>IVA incluido</span>
            <span>{{ formatCRC(cart()?.taxes ?? 0) }}</span>
          </div>
        }
      </section>

      <section class="cart-drawer__promo" aria-label="Código promocional">
        @if (activePromo(); as applied) {
          <div class="cart-drawer__promo-applied">
            <div class="cart-drawer__promo-applied-info">
              <div class="cart-drawer__promo-applied-header">
                <span class="cart-drawer__promo-badge">{{ applied.code }}</span>
                @if (applied.valueLabel) {
                  <span class="cart-drawer__promo-value">−{{ applied.valueLabel }}</span>
                }
              </div>
              @if (applied.description) {
                <p class="cart-drawer__promo-description">{{ applied.description }}</p>
              }
            </div>
            <button
              class="cart-drawer__promo-remove"
              type="button"
              [disabled]="promoPending()"
              (click)="onRemovePromo()"
            >
              Quitar
            </button>
          </div>
        } @else {
          <div class="cart-drawer__promo-form">
            <app-input
              id="cart-promo-code"
              label="Código promocional"
              placeholder="Introduce un código"
              [value]="promoCodeInput()"
              (valueChange)="onPromoInputChange($event)"
            />
            <app-button
              variant="secondary"
              [disabled]="!promoCodeInput().trim()"
              [loading]="promoPending()"
              (click)="onApplyPromo()"
            >
              Aplicar
            </app-button>
          </div>
          @if (promoError(); as err) {
            <p class="cart-drawer__promo-error" role="alert">{{ err }}</p>
          }
        }
      </section>
    }
  </div>

  <footer class="cart-drawer__footer">
    @if (blockedNotice(); as notice) {
      <p class="cart-drawer__footer-notice" role="alert">{{ notice }}</p>
    }
    @if (hasConflicts()) {
      <p class="cart-drawer__footer-notice" role="alert">
        {{ conflictCopy.conflictHint }}
      </p>
    }
    <app-button
      variant="primary"
      size="lg"
      [disabled]="isEmpty() || hasConflicts() || hasBlockedLines()"
      (click)="onCheckoutClick()"
    >
      Finalizar pedido
    </app-button>
  </footer>
</aside>
